// src/services/adminAPI.js - Complete Admin API Service

import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api` || 'http://localhost:5000/api';

// Create axios instance with auth
const adminAPI = axios.create({
  baseURL: `${API_BASE_URL}/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
adminAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
adminAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    
    }
    return Promise.reject(error);
  }
);

class AdminAPI {
  // ========================================
  // 📊 DASHBOARD APIs
  // ========================================
  async getDashboardStats() {
    const response = await adminAPI.get('/dashboard/stats');
    return response.data;
  }

  async getRealTimeStats() {
    const response = await adminAPI.get('/dashboard/realtime/stats');
    return response.data;
  }

  async getAnalytics(period = '30d') {
    const response = await adminAPI.get(`/dashboard/analytics?period=${period}`);
    return response.data;
  }

  async executeQuickAction(action) {
    const response = await adminAPI.post(`/dashboard/quick-actions/${action}`);
    return response.data;
  }

  // ========================================
  // 👥 USER MANAGEMENT APIs
  // ========================================
  async getUsers(params = {}) {
    const response = await adminAPI.get('/users', { params });
    return response.data;
  }

  async getUserById(id) {
    const response = await adminAPI.get(`/users/${id}`);
    return response.data;
  }

  async createUser(userData) {
    const response = await adminAPI.post('/users', userData);
    return response.data;
  }

  async updateUser(id, userData) {
    const response = await adminAPI.put(`/users/${id}`, userData);
    return response.data;
  }

  async deleteUser(id) {
    const response = await adminAPI.delete(`/users/${id}`);
    return response.data;
  }

  async bulkUpdateUsers(data) {
    const response = await adminAPI.put('/users/bulk', data);
    return response.data;
  }

  async exportUsers(format = 'csv') {
    const response = await adminAPI.get(`/users/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // ========================================
  // 🏠 PROPERTY MANAGEMENT APIs
  // ========================================
  async getProperties(params = {}) {
    const response = await adminAPI.get('/properties', { params });
    return response.data;
  }

  async getPropertyById(id) {
    const response = await adminAPI.get(`/properties/${id}`);
    return response.data;
  }

  async createProperty(propertyData) {
    const response = await adminAPI.post('/properties', propertyData);
    return response.data;
  }

  async updateProperty(id, propertyData) {
    const response = await adminAPI.put(`/properties/${id}`, propertyData);
    return response.data;
  }

  async deleteProperty(id) {
    const response = await adminAPI.delete(`/properties/${id}`);
    return response.data;
  }

  async updatePropertyStatus(id, status) {
    const response = await adminAPI.patch(`/properties/${id}/status`, { status });
    return response.data;
  }

  async togglePropertyFeatured(id) {
    const response = await adminAPI.patch(`/properties/${id}/featured`);
    return response.data;
  }

  async bulkDeleteProperties(propertyIds) {
    const response = await adminAPI.delete('/properties/bulk', {
      data: { ids: propertyIds }
    });
    return response.data;
  }

  async bulkUpdateProperties(data) {
    const response = await adminAPI.put('/properties/bulk', data);
    return response.data;
  }

  // ========================================
  // 👨‍💼 AGENT MANAGEMENT APIs
  // ========================================
  async getAgents(params = {}) {
    const response = await adminAPI.get('/agents', { params });
    return response.data;
  }

  async getAgentById(id) {
    const response = await adminAPI.get(`/agents/${id}`);
    return response.data;
  }

  async createAgent(agentData) {
    const response = await adminAPI.post('/agents', agentData);
    return response.data;
  }

  async updateAgent(id, agentData) {
    const response = await adminAPI.put(`/agents/${id}`, agentData);
    return response.data;
  }

  async deleteAgent(id) {
    const response = await adminAPI.delete(`/agents/${id}`);
    return response.data;
  }

  async verifyAgent(id) {
    const response = await adminAPI.patch(`/agents/${id}/verify`);
    return response.data;
  }

  async toggleAgentStatus(id) {
    const response = await adminAPI.patch(`/agents/${id}/toggle-status`);
    return response.data;
  }

  // ========================================
  // 📝 BLOG MANAGEMENT APIs
  // ========================================
  async getBlogs(params = {}) {
    const response = await adminAPI.get('/blogs', { params });
    return response.data;
  }

  async getBlogById(id) {
    const response = await adminAPI.get(`/blogs/${id}`);
    return response.data;
  }

  async createBlog(blogData) {
    const response = await adminAPI.post('/blogs', blogData);
    return response.data;
  }

  async updateBlog(id, blogData) {
    const response = await adminAPI.put(`/blogs/${id}`, blogData);
    return response.data;
  }

  async deleteBlog(id) {
    const response = await adminAPI.delete(`/blogs/${id}`);
    return response.data;
  }

  async publishBlog(id) {
    const response = await adminAPI.patch(`/blogs/${id}/publish`);
    return response.data;
  }

  async toggleBlogFeatured(id) {
    const response = await adminAPI.patch(`/blogs/${id}/featured`);
    return response.data;
  }

  // ========================================
  // 📞 CONTACT MANAGEMENT APIs
  // ========================================
  async getContacts(params = {}) {
    const response = await adminAPI.get('/contacts', { params });
    return response.data;
  }

  async getContactById(id) {
    const response = await adminAPI.get(`/contacts/${id}`);
    return response.data;
  }

  async updateContactStatus(id, status) {
    const response = await adminAPI.patch(`/contacts/${id}/status`, { status });
    return response.data;
  }

  async deleteContact(id) {
    const response = await adminAPI.delete(`/contacts/${id}`);
    return response.data;
  }

  async respondToContact(id, responseData) {
    const response = await adminAPI.post(`/contacts/${id}/respond`, responseData);
    return response.data;
  }

  async bulkUpdateContacts(data) {
    const response = await adminAPI.put('/contacts/bulk', data);
    return response.data;
  }

  // ========================================
  // 📁 FILE UPLOAD APIs
  // ========================================
  async uploadPropertyMedia(formData, onProgress = null) {
    const response = await adminAPI.post('https://client-backend-nvaq.onrender.com/upload/property-media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  }

  async uploadAgentAvatar(formData) {
    const response = await adminAPI.post('/upload/agent-avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async uploadBlogImage(formData) {
    const response = await adminAPI.post('/upload/blog-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // ========================================
  // 📈 ANALYTICS & REPORTS APIs
  // ========================================
  async getDetailedAnalytics(params = {}) {
    const response = await adminAPI.get('/analytics/detailed', { params });
    return response.data;
  }

  async generateReport(type, params = {}) {
    const response = await adminAPI.post('/reports/generate', {
      type,
      ...params
    });
    return response.data;
  }

  async downloadReport(reportId) {
    const response = await adminAPI.get(`/reports/${reportId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // ========================================
  // 🤖 AI FEATURES APIs
  // ========================================
  async generateAIInsights() {
    const response = await adminAPI.post('/ai/insights');
    return response.data;
  }

  async generateAIContent(type, data) {
    const response = await adminAPI.post('/ai/generate-content', {
      type,
      data
    });
    return response.data;
  }

  async optimizeWithAI(entity, id) {
    const response = await adminAPI.post(`/ai/optimize/${entity}/${id}`);
    return response.data;
  }

  // ========================================
  // ⚙️ SYSTEM MANAGEMENT APIs
  // ========================================
  async getSystemSettings() {
    const response = await adminAPI.get('/system/settings');
    return response.data;
  }

  async updateSystemSettings(settings) {
    const response = await adminAPI.put('/system/settings', settings);
    return response.data;
  }

  async performSystemMaintenance(action) {
    const response = await adminAPI.post(`/system/maintenance/${action}`);
    return response.data;
  }

  async getSystemHealth() {
    const response = await adminAPI.get('/system/health');
    return response.data;
  }
}

// ✅ Fix: assign instance before exporting (resolves ESLint error)
const adminAPIInstance = new AdminAPI();
export default adminAPIInstance;
